import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Sidebar, { navForPath } from './Sidebar.js';
import Topbar from './Topbar.js';
import CommandPalette from './CommandPalette.js';
import ViewAsDialog from './ViewAsDialog.js';
import ImpersonationBanner from './ImpersonationBanner.js';
import useHotkey from '../../hooks/useHotkey.js';
import { useDevSidebar } from '../../context/DevSidebarContext.js';
import cn from '../../ui/cn.js';

/**
 * AppShell: persistent sidebar + top bar. Rendered once by App for all
 * authenticated routes so navigation never re-mounts the chrome.
 *
 * Screens render only their content. Use `width="wide"` for dense tables.
 */
export default function AppShell({ children, width = 'default', title }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { toggleCollapsed } = useDevSidebar();
  const location = useLocation();

  const activeNav = navForPath(location.pathname);
  const activeKey = activeNav?.key || 'dashboard';

  useHotkey('mod+k', useCallback(() => setPaletteOpen((open) => !open), []), { allowInInputs: true });
  useHotkey('mod+b', useCallback(() => toggleCollapsed(), [toggleCollapsed]), { allowInInputs: true });

  // Close the mobile sheet on navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-full">
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} activeKey={activeKey} onOpenPalette={() => setPaletteOpen(true)} />

      <div className="ex-shell-main flex min-h-screen flex-col">
        <Topbar title={title || activeNav?.name} onOpenSidebar={() => setMobileOpen(true)} onOpenPalette={() => setPaletteOpen(true)} />
        <ImpersonationBanner />
        <main id="main" className="flex-1">
          <div
            className={cn(
              'mx-auto w-full px-4 py-6 sm:px-6 lg:px-8 lg:py-8 animate-fade-in',
              width === 'wide' ? 'max-w-8xl' : width === 'full' ? 'max-w-none' : 'max-w-7xl',
            )}
          >
            {children}
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ViewAsDialog />
    </div>
  );
}
